import { deleteFromDatabase, putDatabase } from 'store/actions/database.actions';
import { requestQuoteSchema } from 'store/schemas/database.schemas';
import { buildMetaRequestQuoteSelector, buildRequestQuoteSelector } from 'store/selectors/requestQuotes.selector';
import { actionCreators as requestQuoteActionCreators } from 'store/actions/metaDatabase.requestQuotes.actions';
import RequestQuotesRepository from 'services/repositories/RequestQuotesRepository';
import { normalize } from 'normalizr';
import RequestQuoteBundlesRepository from 'services/repositories/RequestQuoteBundlesRepository';
import RequestQuoteBundleComponentsRepository from 'services/repositories/RequestQuoteBundleComponentsRepository';
import { actionCreators as requestActionCreators } from 'store/actions/metaDatabase.requests.actions';

export function deleteRequestQuote(requestQuoteId)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuotesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.deleteRequestQuote(requestQuoteId);

        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        dispatch(deleteFromDatabase(requestQuoteId, requestQuoteSchema.key));
        dispatch(requestQuoteActionCreators._loading(requestQuoteId, false, []));
    };
}

export function storeRequestQuoteBundleComponent(data)
{
    return async (dispatch, getState) =>
    {
        const { requestQuoteId, requestQuoteBundleId } = data;
        const requestQuotesRepository = new RequestQuoteBundleComponentsRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.storeRequestQuoteBundleComponent(data);

        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        const requestQuoteSelector = buildRequestQuoteSelector();
        const requestQuote = requestQuoteSelector(getState(), requestQuoteId);
        const requestQuoteBundles = requestQuote?.requestQuoteBundles?.map((requestQuoteBundle) =>
        {
            if (requestQuoteBundle.id !== requestQuoteBundleId)
            {
                return requestQuoteBundle;
            }
            return { ...requestQuoteBundle, requestQuoteBundleComponents: result.response };
        });

        if (requestQuoteBundles)
        {
            const n = normalize({ ...requestQuote, requestQuoteBundles }, requestQuoteSchema);
            dispatch(putDatabase(n.entities));
        }
    };
}

export function addRequestQuoteBundle(requestQuoteId, requestQuoteBundle)
{
    return async (dispatch, getState) =>
    {
        const requestQuoteSelector = buildRequestQuoteSelector();
        const entity = requestQuoteSelector(getState(), requestQuoteId);
        const requestQuoteBundles = Object.assign([], entity.requestQuoteBundles);
        requestQuoteBundles.push(requestQuoteBundle);

        const n = normalize({ ...entity, requestQuoteBundles }, requestQuoteSchema);
        dispatch(putDatabase(n.entities));
    };
}

export function storeRequestQuoteBundle(data)
{
    return async (dispatch) =>
    {
        const { requestQuoteId } = data;

        const requestQuotesRepository = new RequestQuoteBundlesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.storeRequestQuoteBundle(data);

        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
        }

        return result.response;
    };
}

export function deleteRequestQuoteBundle(requestQuoteId, requestQuoteBundleId)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuoteBundlesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.deleteRequestQuoteBundle(requestQuoteBundleId);

        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
        }

        const requestQuoteSelector = buildRequestQuoteSelector();
        const requestQuote = requestQuoteSelector(getState(), requestQuoteId);
        const requestQuoteBundles = requestQuote?.requestQuoteBundles?.map((requestQuoteBundle) =>
        {
            if (requestQuoteBundle.id === requestQuoteBundleId)
            {
                return null;
            }
            return requestQuoteBundle;
        }).filter((n) => n);

        if (requestQuoteBundles)
        {
            const n = normalize({ ...requestQuote, requestQuoteBundles }, requestQuoteSchema);
            dispatch(putDatabase(n.entities));
        }
    };
}

export function updateRequestQuote(requestQuoteId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuotesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.updateRequestQuote(requestQuoteId, data);

        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        const n = normalize(result.response, requestQuoteSchema);
        dispatch(putDatabase(n.entities));
        dispatch(requestQuoteActionCreators._loading(requestQuoteId, false, []));
    };
}

export function storeInformativeServiceToRequestQuote(requestQuoteId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuotesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.addInformativeService(requestQuoteId, data);

        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        const requestQuoteSelector = buildRequestQuoteSelector();
        const requestQuote = requestQuoteSelector(getState(), requestQuoteId);
        dispatch(requestActionCreators._needUpdate(requestQuote.request_id, true));
    };
}

export function patchInformativeServiceToRequestQuote(requestQuoteId, requestServiceId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuotesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.updateInformativeService(requestQuoteId, requestServiceId, data);

        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        const requestQuoteSelector = buildRequestQuoteSelector();
        const requestQuote = requestQuoteSelector(getState(), requestQuoteId);
        dispatch(requestQuoteActionCreators._loading(requestQuoteId, false, []));
        dispatch(requestActionCreators._needUpdate(requestQuote.request_id, true));
    };
}

export function storeInformativeHotelToRequestQuote(requestQuoteId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuotesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.addInformativeHotel(requestQuoteId, data);
        dispatch(requestQuoteActionCreators._loading(requestQuoteId, false, []));
        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        const requestQuoteSelector = buildRequestQuoteSelector();
        const requestQuote = requestQuoteSelector(getState(), requestQuoteId);
        dispatch(requestActionCreators._needUpdate(requestQuote.request_id, true));
    };
}

export function patchInformativeHotelToRequestQuote(requestQuoteId, requestServiceId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuotesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.updateInformativeHotel(requestQuoteId, requestServiceId, data);
        dispatch(requestQuoteActionCreators._loading(requestQuoteId, false, []));
        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        const requestQuoteSelector = buildRequestQuoteSelector();
        const requestQuote = requestQuoteSelector(getState(), requestQuoteId);
        dispatch(requestActionCreators._needUpdate(requestQuote.request_id, true));
    };
}

export function storeInformativeFlightToRequestQuote(requestQuoteId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuotesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.addInformativeFlight(requestQuoteId, data);
        dispatch(requestQuoteActionCreators._loading(requestQuoteId, false, []));
        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        const requestQuoteSelector = buildRequestQuoteSelector();
        const requestQuote = requestQuoteSelector(getState(), requestQuoteId);
        dispatch(requestActionCreators._needUpdate(requestQuote.request_id, true));
    };
}

export function patchInformativeFlightToRequestQuote(requestQuoteId, requestServiceId, data)
{
    return async (dispatch, getState) =>
    {
        const metaRequestQuoteSelector = buildMetaRequestQuoteSelector();
        const metaEntity = metaRequestQuoteSelector(getState(), requestQuoteId);

        if (metaEntity.loading) return;

        const requestQuotesRepository = new RequestQuotesRepository();

        dispatch(requestQuoteActionCreators._loading(requestQuoteId, true, []));
        const result = await requestQuotesRepository.updateInformativeFlight(requestQuoteId, requestServiceId, data);
        dispatch(requestQuoteActionCreators._loading(requestQuoteId, false, []));
        if (!result.isSuccess())
        {
            dispatch(requestQuoteActionCreators._error(requestQuoteId, result.mergeMessages()));
            return;
        }

        const requestQuoteSelector = buildRequestQuoteSelector();
        const requestQuote = requestQuoteSelector(getState(), requestQuoteId);
        dispatch(requestActionCreators._needUpdate(requestQuote.request_id, true));
    };
}
